import { Component, ErrorHandler, Injector, OnInit, ViewChild, ChangeDetectionStrategy, inject, signal } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { TositeUriService } from '@lemon/core-firebase/service/tosite/tosite-uri.service'

import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'

import { MatDialog } from '@angular/material/dialog'
import { MatSelect } from '@angular/material/select'
import { MatSort } from '@angular/material/sort'

import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of } from 'rxjs'
import { map, startWith, switchMap, tap } from 'rxjs/operators'

import { Asiakas, DownloadSalaryZipRequest, DownloadSalaryZipResponse } from '../_jaettu-lemonator/model/asiakas'

import { TositteidenKuukausisummat } from '@lemon/core-firebase/lemonator/model/tositteiden-lataaminen'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'

import { FieldTree, form, required } from '@angular/forms/signals'
import { MatSnackBar } from '@angular/material/snack-bar'
import { KirjanpitajanRooli } from '@lemon/core-firebase/lemonator/model/kirjanpitaja'
import { NumberMonth, Timestamp } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { DebugService } from 'app/_angular/service/debug.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { HaePalkkatiedotRequest } from 'app/_jaettu-lemonator/model/tyojono'
import { AsiakasService } from '../_angular/service/asiakas/asiakas.service'
import { AsiakkaanTositteidenLataaminenDialog, AsiakkaanTositteidenLataaminenDialogData } from './asiakkaan-tositteiden-lataaminen.dialog'
import { AsiakkaanTositteidenLataushistoriaDialog, AsiakkaanTositteidenLataushistoriaDialogData } from './asiakkaan-tositteiden-lataushistoria.dialog'

export interface TositteenLataamistiedotMaksutavalleEdit {
  maksutavanTunniste: number
  maksutavanNimi: string
  ladattuja: number
  lataamatta: number
}

export interface TositteidenKuukausisummatEdit {
  vuosi: number
  kk: number
  ladattuja: number
  lataamatta: number
  maksutavoittain: TositteenLataamistiedotMaksutavalleEdit[]
  paivitetty: Timestamp
}

interface PalkkatositeLomake {
  selectedMonthZip: NumberMonth | null
  selectedMonthFetch: NumberMonth | null
}

@Component({
  templateUrl: './asiakkaan-tositteet.component.html',
  styleUrls: ['./asiakkaan-tositteet.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsiakkaanTositteetComponent implements OnInit {
  @ViewChild(MatSort) _sort: MatSort
  @ViewChild('naytaKaikki', { read: MatSelect, static: true }) naytaKaikkiSelect: MatSelect

  private ladataanSubject = new BehaviorSubject(true)

  kayttajatNaytettavatKolumnit: string[] = ['etunimi', 'sukunimi', 'email']

  lomake: FieldTree<PalkkatositeLomake>
  private readonly _injector = inject(Injector)

  selectableMonths: NumberMonth[] = []

  asiakasObservable: Observable<Asiakas>
  kuukaudetObservable: Observable<TositteidenKuukausisummatEdit[]>
  eiUusiaTositteitaObservable: Observable<boolean>
  eiTositteitaObservable: Observable<boolean>
  ladataanKuukausiaObservable = this.ladataanSubject.asObservable()

  kuititUriObservable: Observable<string>
  kuititEncodedUriObservable: Observable<string>
  laskutUriObservable: Observable<string>
  laskutEncodedUriObservable: Observable<string>
  kuukausisummakortitUriObservable: Observable<string>
  kuukausisummakortitEncodedUriObservable: Observable<string>
  kirjanpitajaOnDevaajaObservable: Observable<boolean>
  isSuperUserObservable: Observable<boolean>
  isTrustedUserObservable: Observable<boolean>

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _errorHandler: ErrorHandler,
    private _dialog: MatDialog,
    private _firebase: FirebaseLemonator,
    private _asiakasService: AsiakasService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _debugService: DebugService,
    private _ladataanService: LadataanService,
    private _formValidationService: FormValidationService,
    private _snackbar: MatSnackBar,
    private _dateService: DateService,
    private _laskuUriService: LaskuUriService,
    private _tositeUriService: TositeUriService,
  ) {}

  ngOnInit() {
    this.selectableMonths = this._getSelectableMonths()

    this.isSuperUserObservable = this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable.pipe(map(tiedot => tiedot?.rooli === KirjanpitajanRooli.SUPER))
    this.isTrustedUserObservable = this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable.pipe(
      map(tiedot => tiedot?.rooli === KirjanpitajanRooli.SUPER && (tiedot.uid === 'dqUiLNmLRaMNbyNgMhWG1AUmVGI2' || tiedot.uid === '4sNRp7LvWTeZ9WTomoKOA9jD42y1')),
    )

    const previousMonth = this._dateService.lisaaKuukausiaLocalMonth(this._dateService.currentLocalMonth(), -1)
    const previousMonthAsNumber = this._dateService.localMonthToNumber(previousMonth)
    this.lomake = form(
      signal<PalkkatositeLomake>({ selectedMonthZip: previousMonthAsNumber, selectedMonthFetch: previousMonthAsNumber }),
      polku => {
        required(polku.selectedMonthZip)
        required(polku.selectedMonthFetch)
      },
      { injector: this._injector },
    )

    // Debug thingies
    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable
    this.kuititUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot?.avain) {
          return this._tositeUriService.annaTositteidenFirestoreCollectionUri(avainTiedot.avain)
        }
        return ''
      }),
    )
    this.kuititEncodedUriObservable = this.kuititUriObservable.pipe(
      map(uri => {
        return this._debugService.createFirestoreLinkLemonaid(uri)
      }),
    )
    this.laskutUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot?.avain) {
          return this._laskuUriService.getCustomerLaskutCollectionUri(avainTiedot.avain)
        }
        return ''
      }),
    )
    this.laskutEncodedUriObservable = this.laskutUriObservable.pipe(
      map(uri => {
        return this._debugService.createFirestoreLinkLemonaid(uri)
      }),
    )
    this.kuukausisummakortitUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot?.asiakasId) {
          return 'asiakkaat/' + avainTiedot.avain + '/lataamattomat-tositteet-kuukausittain'
        }
        return ''
      }),
    )
    this.kuukausisummakortitEncodedUriObservable = this.kuukausisummakortitUriObservable.pipe(
      map(uri => {
        return this._debugService.createFirestoreLink(uri)
      }),
    )

    this.asiakasObservable = this._asiakasService.nykyinenAsiakasObservable

    this.naytaKaikkiSelect.value = false
    const naytaKaikkiObservable = this.naytaKaikkiSelect.valueChange.asObservable().pipe(startWith(this.naytaKaikkiSelect.value))

    const kuukausisummatObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, naytaKaikkiObservable]).pipe(
      tap(() => {
        setTimeout(() => {
          this.ladataanSubject.next(true)
        }, 0)
      }),
      switchMap(([asiakas, naytaKaikki]) => {
        if (asiakas && asiakas.avain) {
          if (naytaKaikki) {
            return this._firebase.firestoreCollection<TositteidenKuukausisummat>('asiakkaat/' + asiakas.avain + '/lataamattomat-tositteet-kuukausittain').listen()
          } else {
            return this._firebase
              .firestoreCollection<TositteidenKuukausisummat>('asiakkaat/' + asiakas.avain + '/lataamattomat-tositteet-kuukausittain')
              .where('lataamatta', '>', 0)
              .listen()
          }
        }
        return of<TositteidenKuukausisummat[]>([])
      }),
    )

    this.kuukaudetObservable = combineLatest([kuukausisummatObservable, this._asiakasService.nykyisenAsiakkaanJaKayttajienRoolienJaLaskunMaksutavatObservable]).pipe(
      map(([summat, maksutavat]) => {
        const kuukaudetAukiRajaytettyna: TositteidenKuukausisummatEdit[] = []

        for (const alkuperainen of summat) {
          const lisattava: TositteidenKuukausisummatEdit = {
            kk: alkuperainen.kk,
            ladattuja: alkuperainen.ladattuja,
            lataamatta: alkuperainen.lataamatta,
            maksutavoittain: [],
            paivitetty: alkuperainen.paivitetty,
            vuosi: alkuperainen.vuosi,
          }
          for (const mtapa of maksutavat) {
            const alkuperaisesta = alkuperainen.maksutavoittain.filter(maksutavoittain => {
              return maksutavoittain.maksutavanTunniste === mtapa.tunniste
            })

            lisattava.maksutavoittain.push({
              ladattuja: alkuperaisesta.length === 1 ? alkuperaisesta[0].ladattuja : 0,
              lataamatta: alkuperaisesta.length === 1 ? alkuperaisesta[0].lataamatta : 0,
              maksutavanNimi: mtapa.nimi,
              maksutavanTunniste: mtapa.tunniste,
            })
          }
          kuukaudetAukiRajaytettyna.push(lisattava)
        }

        return kuukaudetAukiRajaytettyna.sort((a, b) => {
          if (a.vuosi !== b.vuosi) {
            return b.vuosi - a.vuosi
          }
          return b.kk - a.kk
        })
      }),
      tap(() => {
        setTimeout(() => {
          this.ladataanSubject.next(false)
        }, 0)
      }),
      lemonShare(),
    )

    this.eiUusiaTositteitaObservable = combineLatest([this.kuukaudetObservable, naytaKaikkiObservable, this.ladataanKuukausiaObservable]).pipe(
      map(([kuukaudet, naytaKaikki, ladataan]) => {
        return !ladataan && kuukaudet.length < 1 && !naytaKaikki
      }),
      startWith(false),
    )

    this.eiTositteitaObservable = combineLatest([this.kuukaudetObservable, naytaKaikkiObservable, this.ladataanKuukausiaObservable]).pipe(
      map(([kuukaudet, naytaKaikki, ladataan]) => {
        return !ladataan && kuukaudet.length < 1 && naytaKaikki
      }),
      startWith(false),
    )
  }

  private _getSelectableMonths(): NumberMonth[] {
    const selectableMonths: NumberMonth[] = []

    const currentNumberMonth = this._dateService.numberDateToNumberMonth(this._dateService.currentNumberDate())
    let loopedMonth: NumberMonth = this._dateService.lisaaKuukausiaKuukaudenNumero(currentNumberMonth, -1)

    const endMonth: NumberMonth = 2209

    while (loopedMonth >= endMonth) {
      selectableMonths.push(loopedMonth)
      loopedMonth = this._dateService.lisaaKuukausiaKuukaudenNumero(loopedMonth, -1)
    }
    return selectableMonths
  }

  lataaKaikki(kuukausi: TositteidenKuukausisummatEdit) {
    firstValueFrom(this.asiakasObservable).then(asiakas => {
      if (asiakas) {
        const data: AsiakkaanTositteidenLataaminenDialogData = {
          asiakas: asiakas,
          valittuKuukausi: kuukausi,
          vainLataamattomat: false,
        }

        this._dialog.open(AsiakkaanTositteidenLataaminenDialog, {
          disableClose: true,
          data: data,
        })
      }
    })
  }

  lataaUudet(kuukausi: TositteidenKuukausisummatEdit) {
    firstValueFrom(this.asiakasObservable).then(asiakas => {
      if (asiakas) {
        const data: AsiakkaanTositteidenLataaminenDialogData = {
          asiakas: asiakas,
          valittuKuukausi: kuukausi,
          vainLataamattomat: true,
        }

        this._dialog.open(AsiakkaanTositteidenLataaminenDialog, {
          disableClose: true,
          data: data,
        })
      }
    })
  }

  peruuta() {
    this._router.navigate(['asiakkaat'])
  }

  naytaHistoria() {
    firstValueFrom(this.asiakasObservable).then(asiakas => {
      if (asiakas) {
        const data: AsiakkaanTositteidenLataushistoriaDialogData = {
          asiakas: asiakas,
        }
        this._dialog.open(AsiakkaanTositteidenLataushistoriaDialog, { data: data })
      }
    })
  }

  async fetchSalaryFromPalkkausfi() {
    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this._ladataanService.aloitaLataaminen()

    try {
      const asiakas = await firstValueFrom(this.asiakasObservable)
      const localMonth = this._dateService.numberToLocalMonth(this.lomake.selectedMonthFetch().value())

      const requestData: HaePalkkatiedotRequest = {
        monthToFetch: localMonth,
        asiakasAvain: asiakas.avain,
      }
      await this._firebase.functionsCall<HaePalkkatiedotRequest, void>('palkkausfiPalkkatiedotHaeYksittaisenAsiakkaanTiedot', requestData)

      this._ladataanService.lopetaLataaminen()

      this._snackbar.open('Palkkojen haku käynnistettiin. Haussa kestää tavallisesti n. 5 minuuttia.', 'OK')
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async fetchSalaryFromPalkkausfiSuoriteperusteinen() {
    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this._ladataanService.aloitaLataaminen()

    try {
      const asiakas = await firstValueFrom(this.asiakasObservable)
      const localMonth = this._dateService.numberToLocalMonth(this.lomake.selectedMonthFetch().value())

      const requestData: HaePalkkatiedotRequest = {
        monthToFetch: localMonth,
        asiakasAvain: asiakas.avain,
      }
      await this._firebase.functionsCall<HaePalkkatiedotRequest, void>('palkkausfiPalkkaSuoriteperusteinenHaeYksittaisenAsiakkaanTiedot', requestData)

      this._ladataanService.lopetaLataaminen()

      this._snackbar.open('Palkkojen haku käynnistettiin. Haussa kestää tavallisesti n. 5 minuuttia.', 'OK')
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async downloadSalaryZip() {
    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this._ladataanService.aloitaLataaminen()

    try {
      const asiakas = await firstValueFrom(this.asiakasObservable)
      // const localMonth = this._dateService.numberToLocalMonth(this.lomake.selectedMonthZip().value())

      const requestData: DownloadSalaryZipRequest = {
        month: this.lomake.selectedMonthZip().value(),
        asiakasAvain: asiakas.avain,
      }
      const resp = await this._firebase.functionsCall<DownloadSalaryZipRequest, DownloadSalaryZipResponse>('downloadSalaryZip', requestData, { timeout: 540 * 1000 })

      if (!resp || resp.e) {
        this._errorHandler.handleError('Palkkatositteen lataus epäonnistui: ' + (resp?.e || 'No response'))
        this._snackbar.open('Palkkatositteen lataus epäonnistui. Ole hyvä ja yritä uudelleen.')
        return
      }

      if (!resp.downloadUrl) {
        this._snackbar.open('Annetulle kuukaudelle ei löytynyt palkkatositteita. Jos tämä on mielestäsi virhe, ota yhteys tekniseen tukeen.')
        return
      }

      /** Success, download ZIP */
      document.location = resp.downloadUrl
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }
}
